<template>
  <portal to="modals">
    <div
      v-if="showModal"
      :style="{ zIndex: layer }"
      class="fixed inset-0 z-40 flex items-end justify-center p-4 sm:p-10 sm:items-center"
    >
      <div
        class="absolute inset-0 bg-black opacity-25"
        aria-hidden="true"
        @click="requestClose"
      />
      <div
        ref="dialog"
        role="dialog"
        aria-modal="true"
        :aria-label="label || inferredLabel"
        tabindex="-1"
        :class="['w-full bg-white shadow-2xl pt-6 relative overflow-y-auto max-h-full rounded-lg', size]"
      >
        <slot />
      </div>
    </div>
  </portal>
</template>

<script>
const openDialogs = []
let originalOverflow = ''
let nextLayer = 1000
const controls = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
export default {
  props: {
    open: { type: Boolean, default: false },
    canClose: { type: Boolean, default: true },
    size: { type: String, default: 'max-w-xl' },
    label: { type: String, default: null }
  },
  data() { return { layer: 1000, showModal: false, inferredLabel: 'Dialog', previousFocus: null, focusTimer: null } },
  watch: {
    open: { immediate: true, handler(value) { if (value) { this.show() } else { this.hide() } } }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown, true)
    document.addEventListener('focusin', this.onFocus, true)
  },
  beforeDestroy() {
    clearTimeout(this.focusTimer)
    document.removeEventListener('keydown', this.onKeydown, true)
    document.removeEventListener('focusin', this.onFocus, true)
    this.hide()
  },
  methods: {
    topmost() { return openDialogs[openDialogs.length - 1] === this },
    focusable() {
      return this.$refs.dialog ? Array.from(this.$refs.dialog.querySelectorAll(controls)).filter(el => el.getClientRects().length && !el.closest('[inert]')) : []
    },
    show() {
      if (this.showModal) { return }
      this.previousFocus = document.activeElement
      if (!openDialogs.length) { originalOverflow = document.body.style.overflow }
      this.layer = ++nextLayer
      openDialogs.push(this)
      document.body.style.overflow = 'hidden'
      this.showModal = true
      // Portal content is mounted on a subsequent render tick.
      this.$nextTick(() => {
        this.focusTimer = setTimeout(() => {
          if (!this.showModal || !this.topmost() || !this.$refs.dialog) { return }
          const heading = this.$refs.dialog.querySelector('h1,h2,h3,h4,[role="heading"]')
          this.inferredLabel = heading ? heading.textContent.trim() : 'Dialog'
          this.$refs.dialog.focus()
        }, 0)
      })
    },
    hide() {
      if (!this.showModal) { return }
      const wasTop = this.topmost()
      const index = openDialogs.indexOf(this)
      if (index >= 0) { openDialogs.splice(index, 1) }
      this.showModal = false
      clearTimeout(this.focusTimer)
      if (!openDialogs.length) { document.body.style.overflow = originalOverflow }
      this.$nextTick(() => {
        if (wasTop && this.previousFocus && this.previousFocus.isConnected) { this.previousFocus.focus() }
      })
    },
    requestClose() { if (this.canClose && this.topmost()) { this.hide(); this.$emit('close') } },
    onFocus(event) {
      if (this.showModal && this.topmost() && this.$refs.dialog && !this.$refs.dialog.contains(event.target)) { this.$refs.dialog.focus() }
    },
    onKeydown(event) {
      if (!this.showModal || !this.topmost()) { return }
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.requestClose(); return }
      if (event.key !== 'Tab') { return }
      const items = this.focusable()
      const first = items[0]; const last = items[items.length - 1]
      if (!first) { event.preventDefault(); this.$refs.dialog.focus(); return }
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === this.$refs.dialog)) { event.preventDefault(); last.focus() } else if (!event.shiftKey && (active === last || active === this.$refs.dialog)) { event.preventDefault(); first.focus() }
    }
  }
}
</script>
